import React, { useState, useEffect, useCallback } from 'react';
import styles from './PermitsModule.module.css';
import { Project } from '../../types';
import { permitsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface PermitsModuleProps {
  project: Project;
}

interface PermitItem {
  _id: string;
  title: string;
  type: string;
  status: string;
  referenceNumber?: string;
  priority: string;
  description?: string;
  authority?: { name?: string; type?: string };
  location?: { address?: string; city?: string };
  dates?: {
    submittedAt?: string;
    expectedDecision?: string;
    objectionDeadline?: string;
    publicationEnd?: string;
    validFrom?: string;
    validUntil?: string;
  };
  procedures?: any[];
  objections?: any[];
  conditions?: any[];
  tags?: string[];
  createdAt: string;
}

interface TimelineEvent {
  permitId: string;
  permitTitle: string;
  type: string;
  label: string;
  date: string;
  isPast: boolean;
  daysRemaining: number;
}

const TYPE_LABELS: Record<string, string> = {
  omgevingsvergunning: 'Omgevingsvergunning', watervergunning: 'Watervergunning',
  natuurvergunning: 'Natuurvergunning', kapvergunning: 'Kapvergunning',
  sloopmelding: 'Sloopmelding', milieumelding: 'Milieumelding',
  bestemmingsplan_wijziging: 'Bestemmingsplanwijziging', verkeersbesluit: 'Verkeersbesluit',
  evenementenvergunning: 'Evenementenvergunning', overig: 'Overig'
};

const STATUS_LABELS: Record<string, string> = {
  concept: 'Concept', ingediend: 'Ingediend', in_behandeling: 'In behandeling',
  ter_inzage: 'Ter inzage', bezwaarperiode: 'Bezwaarperiode', bezwaar_ingediend: 'Bezwaar ingediend',
  beroep: 'Beroep', verleend: 'Verleend', geweigerd: 'Geweigerd', ingetrokken: 'Ingetrokken'
};

const PRIORITY_LABELS: Record<string, string> = { low: 'Laag', medium: 'Normaal', high: 'Hoog', critical: 'Kritiek' };

const AUTHORITY_TYPE_LABELS: Record<string, string> = {
  gemeente: 'Gemeente', provincie: 'Provincie', waterschap: 'Waterschap',
  rijksoverheid: 'Rijksoverheid', omgevingsdienst: 'Omgevingsdienst', overig: 'Overig'
};

export const PermitsModule: React.FC<PermitsModuleProps> = ({ project }) => {
  const toast = useToast();
  const [permits, setPermits] = useState<PermitItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [upcoming, setUpcoming] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'timeline' | 'create' | 'detail'>('list');
  const [selectedPermit, setSelectedPermit] = useState<PermitItem | null>(null);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const [formData, setFormData] = useState({
    title: '', type: 'omgevingsvergunning', description: '', priority: 'medium',
    referenceNumber: '', authority: { name: '', type: 'gemeente' },
    location: { address: '', city: '' },
    dates: { submittedAt: '', expectedDecision: '', objectionDeadline: '' }
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [permitsData, timelineData] = await Promise.all([
        permitsAPI.getByProject(project.id),
        permitsAPI.getTimeline(project.id).catch(() => ({ timeline: [], upcoming: [] }))
      ]);
      setPermits(permitsData.permits || []);
      setTimeline(timelineData.timeline || []);
      setUpcoming(timelineData.upcoming || []);
    } catch (error) {
      console.error('Failed to fetch permits:', error);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await permitsAPI.create({ ...formData, project: project.id });
      toast.success('Vergunning aangemaakt');
      setView('list');
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await permitsAPI.delete(id);
      toast.success('Vergunning verwijderd');
      fetchData();
      if (selectedPermit?._id === id) setView('list');
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await permitsAPI.update(id, { status: newStatus });
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const startEditing = () => {
    if (!selectedPermit) return;
    setEditData({
      title: selectedPermit.title || '',
      type: selectedPermit.type || 'omgevingsvergunning',
      description: selectedPermit.description || '',
      priority: selectedPermit.priority || 'medium',
      referenceNumber: selectedPermit.referenceNumber || '',
      authority: { name: selectedPermit.authority?.name || '', type: selectedPermit.authority?.type || 'gemeente' },
      location: { address: selectedPermit.location?.address || '', city: selectedPermit.location?.city || '' },
      dates: {
        submittedAt: selectedPermit.dates?.submittedAt ? selectedPermit.dates.submittedAt.split('T')[0] : '',
        expectedDecision: selectedPermit.dates?.expectedDecision ? selectedPermit.dates.expectedDecision.split('T')[0] : '',
        objectionDeadline: selectedPermit.dates?.objectionDeadline ? selectedPermit.dates.objectionDeadline.split('T')[0] : '',
        publicationEnd: selectedPermit.dates?.publicationEnd ? selectedPermit.dates.publicationEnd.split('T')[0] : '',
        validFrom: selectedPermit.dates?.validFrom ? selectedPermit.dates.validFrom.split('T')[0] : '',
        validUntil: selectedPermit.dates?.validUntil ? selectedPermit.dates.validUntil.split('T')[0] : '',
      },
      tags: selectedPermit.tags || [],
      notes: (selectedPermit as any).notes || '',
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData(null);
  };

  const saveEditing = async () => {
    if (!selectedPermit || !editData) return;
    try {
      setSaving(true);
      const updated = await permitsAPI.update(selectedPermit._id, editData);
      setSelectedPermit(updated);
      setEditing(false);
      setEditData(null);
      toast.success('Vergunning bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const updateEdit = (field: string, value: any) => {
    setEditData((prev: any) => {
      const parts = field.split('.');
      if (parts.length === 1) return { ...prev, [field]: value };
      const copy = { ...prev };
      copy[parts[0]] = { ...copy[parts[0]], [parts[1]]: value };
      return copy;
    });
  };

  const addTag = (tag: string) => {
    if (!tag.trim() || editData.tags.includes(tag.trim())) return;
    setEditData((prev: any) => ({ ...prev, tags: [...prev.tags, tag.trim()] }));
  };

  const removeTag = (idx: number) => {
    setEditData((prev: any) => ({ ...prev, tags: prev.tags.filter((_: any, i: number) => i !== idx) }));
  };

  const addProcedure = async (step: string) => {
    if (!selectedPermit || !step.trim()) return;
    try {
      const updated = await permitsAPI.addProcedure(selectedPermit._id, { step: step.trim(), status: 'pending' });
      setSelectedPermit(updated);
      toast.success('Procedurestap toegevoegd');
    } catch (error) {
      toast.error('Fout bij toevoegen');
    }
  };

  const addObjection = async (submittedBy: string, summary: string) => {
    if (!selectedPermit || !submittedBy.trim() || !summary.trim()) return;
    try {
      const updated = await permitsAPI.addObjection(selectedPermit._id, { submittedBy: submittedBy.trim(), summary: summary.trim() });
      setSelectedPermit(updated);
      toast.success('Bezwaar toegevoegd');
    } catch (error) {
      toast.error('Fout bij toevoegen');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', type: 'omgevingsvergunning', description: '', priority: 'medium', referenceNumber: '', authority: { name: '', type: 'gemeente' }, location: { address: '', city: '' }, dates: { submittedAt: '', expectedDecision: '', objectionDeadline: '' } });
  };

  const viewDetail = async (permit: PermitItem) => {
    try {
      const full = await permitsAPI.getById(permit._id);
      setSelectedPermit(full);
      setView('detail');
    } catch (error) {
      toast.error('Fout bij laden');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      concept: '#94a3b8', ingediend: '#3b82f6', in_behandeling: '#f59e0b',
      ter_inzage: '#8b5cf6', bezwaarperiode: '#ef4444', bezwaar_ingediend: '#dc2626',
      beroep: '#dc2626', verleend: '#22c55e', geweigerd: '#ef4444', ingetrokken: '#94a3b8'
    };
    return colors[status] || '#94a3b8';
  };

  const filteredPermits = filter === 'all' ? permits : permits.filter(p => p.status === filter);

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Laden...</div></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Vergunningen Tracker</h1>
          <p className={styles.subtitle}>Volg vergunningsprocedures en deadlines</p>
        </div>
        <div className={styles.headerActions}>
          {view === 'list' || view === 'timeline' ? (
            <>
              <div className={styles.viewToggle}>
                <button className={`${styles.toggleButton} ${view === 'list' ? styles.toggleActive : ''}`} onClick={() => setView('list')}>Lijst</button>
                <button className={`${styles.toggleButton} ${view === 'timeline' ? styles.toggleActive : ''}`} onClick={() => setView('timeline')}>Timeline</button>
              </div>
              <button className={styles.primaryButton} onClick={() => setView('create')}>+ Nieuwe Vergunning</button>
            </>
          ) : (
            <button className={styles.secondaryButton} onClick={() => { setView('list'); setSelectedPermit(null); }}>Terug</button>
          )}
        </div>
      </div>

      {/* Upcoming deadlines alert */}
      {upcoming.length > 0 && (view === 'list' || view === 'timeline') && (
        <div className={styles.alertBanner}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>{upcoming.length} naderende deadline{upcoming.length > 1 ? 's' : ''}: </span>
          {upcoming.slice(0, 3).map((u, i) => (
            <span key={i} className={styles.alertItem}>
              {u.permitTitle} - {u.label} ({u.daysRemaining > 0 ? `${u.daysRemaining} dagen` : 'Vandaag!'}){i < Math.min(2, upcoming.length - 1) ? ', ' : ''}
            </span>
          ))}
        </div>
      )}

      {/* Stats */}
      {(view === 'list' || view === 'timeline') && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}><span className={styles.statValue}>{permits.length}</span><span className={styles.statLabel}>Totaal</span></div>
          <div className={styles.statCard}><span className={styles.statValue}>{permits.filter(p => ['ingediend', 'in_behandeling', 'ter_inzage'].includes(p.status)).length}</span><span className={styles.statLabel}>In procedure</span></div>
          <div className={styles.statCard}><span className={styles.statValue}>{permits.filter(p => p.status === 'verleend').length}</span><span className={styles.statLabel}>Verleend</span></div>
          <div className={styles.statCard}><span className={styles.statValue}>{permits.filter(p => ['bezwaarperiode', 'bezwaar_ingediend', 'beroep'].includes(p.status)).length}</span><span className={styles.statLabel}>Bezwaar/Beroep</span></div>
        </div>
      )}

      {view === 'list' && (
        <>
          <div className={styles.filters}>
            {['all', 'concept', 'ingediend', 'in_behandeling', 'ter_inzage', 'verleend'].map(f => (
              <button key={f} className={`${styles.filterButton} ${filter === f ? styles.filterActive : ''}`} onClick={() => setFilter(f)}>
                {f === 'all' ? 'Alles' : STATUS_LABELS[f]}
              </button>
            ))}
          </div>

          {filteredPermits.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M12 18v-6"/><path d="M9 15h6"/></svg>
              <h3>Geen vergunningen</h3>
              <p>Voeg een vergunning toe om procedures te volgen.</p>
            </div>
          ) : (
            <div className={styles.list}>
              {filteredPermits.map(permit => (
                <div key={permit._id} className={styles.card} onClick={() => viewDetail(permit)}>
                  <div className={styles.cardLeft}>
                    <div className={styles.statusDot} style={{ background: getStatusColor(permit.status) }} />
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.cardHeader}>
                      <h3>{permit.title}</h3>
                      {permit.referenceNumber && <span className={styles.refNumber}>{permit.referenceNumber}</span>}
                    </div>
                    <div className={styles.cardMeta}>
                      <span className={styles.badge} style={{ background: getStatusColor(permit.status) + '20', color: getStatusColor(permit.status) }}>{STATUS_LABELS[permit.status]}</span>
                      <span className={styles.typeBadge}>{TYPE_LABELS[permit.type]}</span>
                      <span className={`${styles.priorityBadge} ${styles[`priority_${permit.priority}`]}`}>{PRIORITY_LABELS[permit.priority]}</span>
                    </div>
                    {permit.authority?.name && <p className={styles.authority}>Bevoegd gezag: {permit.authority.name}</p>}
                  </div>
                  <div className={styles.cardRight}>
                    {permit.dates?.expectedDecision && (
                      <div className={styles.deadline}>
                        <span className={styles.deadlineLabel}>Verwacht besluit</span>
                        <span>{new Date(permit.dates.expectedDecision).toLocaleDateString('nl-NL')}</span>
                      </div>
                    )}
                    <button className={styles.deleteSmall} onClick={e => { e.stopPropagation(); handleDelete(permit._id); }}>Verwijder</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'timeline' && (
        <div className={styles.timelineView}>
          {timeline.length === 0 ? (
            <div className={styles.emptyState}><h3>Geen deadlines</h3><p>Er zijn nog geen vergunningen met deadlines.</p></div>
          ) : (
            <div className={styles.timelineList}>
              {timeline.map((event, idx) => (
                <div key={idx} className={`${styles.timelineItem} ${event.isPast ? styles.timelinePast : ''}`}>
                  <div className={styles.timelineDot} style={{ background: event.isPast ? '#94a3b8' : event.daysRemaining <= 7 ? '#ef4444' : '#22c55e' }} />
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineDate}>{new Date(event.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                    <h4>{event.permitTitle}</h4>
                    <span className={styles.timelineLabel}>{event.label}</span>
                    {!event.isPast && (
                      <span className={`${styles.daysRemaining} ${event.daysRemaining <= 7 ? styles.urgent : ''}`}>
                        {event.daysRemaining} dagen
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'create' && (
        <div className={styles.createForm}>
          <h2>Nieuwe Vergunning</h2>
          <div className={styles.formGroup}>
            <label>Titel *</label>
            <input type="text" value={formData.title} onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Bijv. Omgevingsvergunning bouwactiviteit" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Type</label>
              <select value={formData.type} onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}>
                {Object.entries(TYPE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Prioriteit</label>
              <select value={formData.priority} onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}>
                {Object.entries(PRIORITY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Beschrijving</label>
            <textarea value={formData.description} onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Referentienummer</label>
              <input type="text" value={formData.referenceNumber} onChange={e => setFormData(prev => ({ ...prev, referenceNumber: e.target.value }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Bevoegd gezag</label>
              <input type="text" value={formData.authority.name} onChange={e => setFormData(prev => ({ ...prev, authority: { ...prev.authority, name: e.target.value } }))} placeholder="Bijv. Gemeente Amsterdam" />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Indieningsdatum</label>
              <input type="date" value={formData.dates.submittedAt} onChange={e => setFormData(prev => ({ ...prev, dates: { ...prev.dates, submittedAt: e.target.value } }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Verwacht besluit</label>
              <input type="date" value={formData.dates.expectedDecision} onChange={e => setFormData(prev => ({ ...prev, dates: { ...prev.dates, expectedDecision: e.target.value } }))} />
            </div>
            <div className={styles.formGroup}>
              <label>Bezwaartermijn</label>
              <input type="date" value={formData.dates.objectionDeadline} onChange={e => setFormData(prev => ({ ...prev, dates: { ...prev.dates, objectionDeadline: e.target.value } }))} />
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.secondaryButton} onClick={() => { setView('list'); resetForm(); }}>Annuleren</button>
            <button className={styles.primaryButton} onClick={handleCreate} disabled={!formData.title}>Aanmaken</button>
          </div>
        </div>
      )}

      {view === 'detail' && selectedPermit && (
        <DetailView
          permit={selectedPermit}
          editing={editing}
          saving={saving}
          editData={editData}
          onStartEditing={startEditing}
          onCancelEditing={cancelEditing}
          onSaveEditing={saveEditing}
          onUpdateEdit={updateEdit}
          onStatusChange={handleStatusChange}
          onAddTag={addTag}
          onRemoveTag={removeTag}
          onAddProcedure={addProcedure}
          onAddObjection={addObjection}
          onDelete={handleDelete}
          getStatusColor={getStatusColor}
        />
      )}
    </div>
  );
};

/* ─── Detail View Component ─── */
interface DetailViewProps {
  permit: PermitItem;
  editing: boolean;
  saving: boolean;
  editData: any;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEditing: () => void;
  onUpdateEdit: (field: string, value: any) => void;
  onStatusChange: (id: string, status: string) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (idx: number) => void;
  onAddProcedure: (step: string) => void;
  onAddObjection: (submittedBy: string, summary: string) => void;
  onDelete: (id: string) => void;
  getStatusColor: (status: string) => string;
}

const DetailView: React.FC<DetailViewProps> = ({
  permit, editing, saving, editData, onStartEditing, onCancelEditing, onSaveEditing,
  onUpdateEdit, onStatusChange, onAddTag, onRemoveTag, onAddProcedure, onAddObjection,
  onDelete, getStatusColor
}) => {
  const [newProcedureStep, setNewProcedureStep] = useState('');
  const [newObjection, setNewObjection] = useState({ submittedBy: '', summary: '' });
  const [showAddProcedure, setShowAddProcedure] = useState(false);
  const [showAddObjection, setShowAddObjection] = useState(false);
  const [newTag, setNewTag] = useState('');

  return (
    <div className={styles.detailView}>
      <div className={styles.detailHeader}>
        <div style={{ flex: 1 }}>
          {editing ? (
            <>
              <input className={styles.editInputTitle} value={editData.title} onChange={e => onUpdateEdit('title', e.target.value)} placeholder="Titel" />
              <div className={styles.editMetaRow}>
                <select className={styles.editSelect} value={editData.type} onChange={e => onUpdateEdit('type', e.target.value)}>
                  {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select className={styles.editSelect} value={editData.priority} onChange={e => onUpdateEdit('priority', e.target.value)}>
                  {Object.entries(PRIORITY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input className={styles.editInput} value={editData.referenceNumber} onChange={e => onUpdateEdit('referenceNumber', e.target.value)} placeholder="Referentienummer" style={{ maxWidth: 200 }} />
              </div>
            </>
          ) : (
            <>
              <h2>{permit.title}</h2>
              <div className={styles.detailMeta}>
                <span className={styles.badge} style={{ background: getStatusColor(permit.status) + '20', color: getStatusColor(permit.status) }}>{STATUS_LABELS[permit.status]}</span>
                <span className={styles.typeBadge}>{TYPE_LABELS[permit.type]}</span>
                <span className={`${styles.priorityBadge} ${styles[`priority_${permit.priority}`]}`}>{PRIORITY_LABELS[permit.priority]}</span>
                {permit.referenceNumber && <span className={styles.refNumber}>{permit.referenceNumber}</span>}
              </div>
            </>
          )}
        </div>
        <div className={styles.detailActions}>
          {editing ? (
            <>
              <button className={styles.secondaryButton} onClick={onCancelEditing} disabled={saving}>Annuleren</button>
              <button className={styles.primaryButton} onClick={onSaveEditing} disabled={saving}>{saving ? 'Opslaan...' : 'Opslaan'}</button>
            </>
          ) : (
            <>
              <select value={permit.status} onChange={e => onStatusChange(permit._id, e.target.value)} className={styles.statusSelect}>
                {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
              <button className={styles.editBtn} onClick={onStartEditing}>Bewerken</button>
            </>
          )}
        </div>
      </div>

      {/* Description */}
      {editing ? (
        <div className={styles.formGroup}>
          <label>Beschrijving</label>
          <textarea className={styles.editTextarea} value={editData.description} onChange={e => onUpdateEdit('description', e.target.value)} rows={3} />
        </div>
      ) : (
        permit.description && <p className={styles.detailDescription}>{permit.description}</p>
      )}

      {/* Info Grid */}
      {editing ? (
        <div className={styles.editSection}>
          <h3>Gegevens</h3>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Bevoegd gezag</label>
              <input type="text" value={editData.authority.name} onChange={e => onUpdateEdit('authority.name', e.target.value)} placeholder="Bijv. Gemeente Amsterdam" />
            </div>
            <div className={styles.formGroup}>
              <label>Type bevoegd gezag</label>
              <select value={editData.authority.type} onChange={e => onUpdateEdit('authority.type', e.target.value)}>
                {Object.entries(AUTHORITY_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Adres</label>
              <input type="text" value={editData.location.address} onChange={e => onUpdateEdit('location.address', e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label>Plaats</label>
              <input type="text" value={editData.location.city} onChange={e => onUpdateEdit('location.city', e.target.value)} />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.detailGrid}>
          {permit.authority?.name && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Bevoegd gezag</span><span>{permit.authority.name}</span></div>
          )}
          {permit.location?.address && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Locatie</span><span>{permit.location.address}{permit.location.city ? `, ${permit.location.city}` : ''}</span></div>
          )}
          {permit.dates?.submittedAt && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Ingediend</span><span>{new Date(permit.dates.submittedAt).toLocaleDateString('nl-NL')}</span></div>
          )}
          {permit.dates?.expectedDecision && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Verwacht besluit</span><span>{new Date(permit.dates.expectedDecision).toLocaleDateString('nl-NL')}</span></div>
          )}
          {permit.dates?.objectionDeadline && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Bezwaartermijn</span><span>{new Date(permit.dates.objectionDeadline).toLocaleDateString('nl-NL')}</span></div>
          )}
          {permit.dates?.publicationEnd && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Einde ter inzage</span><span>{new Date(permit.dates.publicationEnd).toLocaleDateString('nl-NL')}</span></div>
          )}
          {permit.dates?.validFrom && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Geldig vanaf</span><span>{new Date(permit.dates.validFrom).toLocaleDateString('nl-NL')}</span></div>
          )}
          {permit.dates?.validUntil && (
            <div className={styles.detailItem}><span className={styles.detailLabel}>Geldig tot</span><span>{new Date(permit.dates.validUntil).toLocaleDateString('nl-NL')}</span></div>
          )}
        </div>
      )}

      {/* Dates editing */}
      {editing && (
        <div className={styles.editSection}>
          <h3>Data</h3>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label>Indieningsdatum</label><input type="date" value={editData.dates.submittedAt} onChange={e => onUpdateEdit('dates.submittedAt', e.target.value)} /></div>
            <div className={styles.formGroup}><label>Verwacht besluit</label><input type="date" value={editData.dates.expectedDecision} onChange={e => onUpdateEdit('dates.expectedDecision', e.target.value)} /></div>
            <div className={styles.formGroup}><label>Bezwaartermijn</label><input type="date" value={editData.dates.objectionDeadline} onChange={e => onUpdateEdit('dates.objectionDeadline', e.target.value)} /></div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label>Einde ter inzage</label><input type="date" value={editData.dates.publicationEnd} onChange={e => onUpdateEdit('dates.publicationEnd', e.target.value)} /></div>
            <div className={styles.formGroup}><label>Geldig vanaf</label><input type="date" value={editData.dates.validFrom} onChange={e => onUpdateEdit('dates.validFrom', e.target.value)} /></div>
            <div className={styles.formGroup}><label>Geldig tot</label><input type="date" value={editData.dates.validUntil} onChange={e => onUpdateEdit('dates.validUntil', e.target.value)} /></div>
          </div>
        </div>
      )}

      {/* Notes */}
      {editing ? (
        <div className={styles.editSection}>
          <h3>Notities</h3>
          <div className={styles.formGroup}>
            <textarea className={styles.editTextarea} value={editData.notes} onChange={e => onUpdateEdit('notes', e.target.value)} rows={3} placeholder="Notities..." />
          </div>
        </div>
      ) : (
        (permit as any).notes && (
          <div className={styles.section}>
            <h3>Notities</h3>
            <p className={styles.detailDescription}>{(permit as any).notes}</p>
          </div>
        )
      )}

      {/* Tags */}
      {editing ? (
        <div className={styles.editSection}>
          <h3>Tags</h3>
          <div className={styles.tagList}>
            {editData.tags.map((tag: string, idx: number) => (
              <span key={idx} className={styles.tag}>{tag}<button className={styles.tagRemove} onClick={() => onRemoveTag(idx)}>&times;</button></span>
            ))}
          </div>
          <div className={styles.addTagRow}>
            <input className={styles.editInput} value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Nieuwe tag" onKeyDown={e => { if (e.key === 'Enter') { onAddTag(newTag); setNewTag(''); }}} />
            <button className={styles.addBtn} onClick={() => { onAddTag(newTag); setNewTag(''); }}>+</button>
          </div>
        </div>
      ) : (
        permit.tags && permit.tags.length > 0 && (
          <div className={styles.section}>
            <h3>Tags</h3>
            <div className={styles.tagList}>
              {permit.tags.map((tag, idx) => <span key={idx} className={styles.tag}>{tag}</span>)}
            </div>
          </div>
        )
      )}

      {/* Procedures */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Procedurestappen {permit.procedures && permit.procedures.length > 0 ? `(${permit.procedures.length})` : ''}</h3>
          {!editing && <button className={styles.addBtn} onClick={() => setShowAddProcedure(!showAddProcedure)}>+ Stap toevoegen</button>}
        </div>
        {permit.procedures && permit.procedures.map((proc: any, idx: number) => (
          <div key={idx} className={styles.procedureStep}>
            <span className={`${styles.stepStatus} ${styles[`step_${proc.status}`]}`}>{proc.status === 'completed' ? 'Afgerond' : proc.status === 'active' ? 'Actief' : proc.status === 'skipped' ? 'Overgeslagen' : 'Wacht'}</span>
            <span>{proc.step}</span>
          </div>
        ))}
        {showAddProcedure && (
          <div className={styles.addInlineForm}>
            <input className={styles.editInput} value={newProcedureStep} onChange={e => setNewProcedureStep(e.target.value)} placeholder="Procedurestap..." onKeyDown={e => { if (e.key === 'Enter' && newProcedureStep.trim()) { onAddProcedure(newProcedureStep); setNewProcedureStep(''); setShowAddProcedure(false); }}} />
            <button className={styles.primaryButton} onClick={() => { if (newProcedureStep.trim()) { onAddProcedure(newProcedureStep); setNewProcedureStep(''); setShowAddProcedure(false); }}}>Toevoegen</button>
          </div>
        )}
      </div>

      {/* Objections */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Bezwaren {permit.objections && permit.objections.length > 0 ? `(${permit.objections.length})` : ''}</h3>
          {!editing && <button className={styles.addBtn} onClick={() => setShowAddObjection(!showAddObjection)}>+ Bezwaar toevoegen</button>}
        </div>
        {permit.objections && permit.objections.map((obj: any, idx: number) => (
          <div key={idx} className={styles.objectionItem}>
            <div className={styles.objectionHeader}>
              <strong>{obj.submittedBy}</strong>
              <span>{new Date(obj.submittedAt).toLocaleDateString('nl-NL')}</span>
            </div>
            <p>{obj.summary}</p>
          </div>
        ))}
        {showAddObjection && (
          <div className={styles.addInlineForm}>
            <input className={styles.editInput} value={newObjection.submittedBy} onChange={e => setNewObjection(prev => ({ ...prev, submittedBy: e.target.value }))} placeholder="Ingediend door..." />
            <textarea className={styles.editTextarea} value={newObjection.summary} onChange={e => setNewObjection(prev => ({ ...prev, summary: e.target.value }))} placeholder="Samenvatting bezwaar..." rows={2} />
            <button className={styles.primaryButton} onClick={() => { if (newObjection.submittedBy.trim() && newObjection.summary.trim()) { onAddObjection(newObjection.submittedBy, newObjection.summary); setNewObjection({ submittedBy: '', summary: '' }); setShowAddObjection(false); }}}>Toevoegen</button>
          </div>
        )}
      </div>

      {/* Delete */}
      {!editing && (
        <div className={styles.dangerZone}>
          <button className={styles.deleteBtn} onClick={() => { if (window.confirm('Weet je zeker dat je deze vergunning wilt verwijderen?')) onDelete(permit._id); }}>Vergunning verwijderen</button>
        </div>
      )}
    </div>
  );
};
